import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Row,
  Select,
  Space,
  Switch,
  Table,
  Tag,
  TimePicker,
  Tooltip,
  Typography,
  notification,
} from "antd";
import type { ColumnsType } from "antd/es/table";
import {
  DeleteOutlined,
  EditOutlined,
  PlusOutlined,
  ReloadOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import api from "shared/api/client";
import "./PartyOrderPage.css";

interface PartyOrder {
  _id: string;
  orderDate?: string;
  note?: string;
  extra?: string;
  customerName: string;
  eventType?: string;
  decoration?: string;
  childGender?: string;
  eventDate: string;
  eventTime: string;
  packageName?: string;
  location?: string;
  peopleCount?: string;
  prePay?: number;
  studioBooked?: boolean;
  studioDateTime?: string;
  nameToPrint?: string;
  moneyNote?: string;
}

type PartyOrderFormValues = Omit<
  Partial<PartyOrder>,
  "orderDate" | "eventDate" | "eventTime"
> & {
  orderDate?: any;
  eventDate?: any;
  eventTime?: any;
};

const eventTypeOptions = [
  { label: "1 нас", value: "1 нас" },
  { label: "2 нас", value: "2 нас" },
  { label: "Сэвлэг", value: "Сэвлэг" },
  { label: "Үсэглэл", value: "Үсэглэл" },
  { label: "Төрсөн өдөр", value: "Төрсөн өдөр" },
  { label: "Бусад", value: "Бусад" },
];

const genderOptions = [
  { label: "Хүү", value: "Хүү" },
  { label: "Охин", value: "Охин" },
];

const packageOptions = [
  { label: "Багц 1", value: "Багц 1" },
  { label: "Багц 2", value: "Багц 2" },
  { label: "Багц 3", value: "Багц 3" },
];

const locationOptions = [
  { label: "VIP", value: "VIP" },
  { label: "Зааланд", value: "Зааланд" },
  { label: "Студи", value: "Студи" },
  { label: "Гадуур", value: "Гадуур" },
];

const parseStoredDate = (value?: string) => {
  if (!value) return undefined;
  const parsed = dayjs(value.replace(/\./g, "-"));
  return parsed.isValid() ? parsed : undefined;
};

const parseStoredTime = (value?: string) => {
  if (!value) return undefined;
  const parsed = dayjs(`2000-01-01 ${value}`);
  return parsed.isValid() ? parsed : undefined;
};

const toCurrency = (value?: number) => `${(value || 0).toLocaleString()}₮`;

const PartyOrderPage: React.FC = () => {
  const [data, setData] = useState<PartyOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingOrder, setEditingOrder] = useState<PartyOrder | null>(null);
  const [submitting, setSubmitting] = useState(false);
  const [searchText, setSearchText] = useState("");
  const [selectedEventType, setSelectedEventType] = useState<string | null>(
    null
  );
  const [form] = Form.useForm<PartyOrderFormValues>();

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await api.get("/party-order/get");
      setData(res.data.data || []);
    } catch (error: any) {
      notification.error({
        message: "Алдаа",
        description:
          error.response?.data?.message || "Жагсаалт татахад алдаа гарлаа",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const filteredData = useMemo(() => {
    const normalizedSearch = searchText.trim().toLowerCase();

    return data.filter((order) => {
      const matchesEventType =
        !selectedEventType || order.eventType === selectedEventType;

      const searchableText = [
        order.customerName,
        order.eventDate,
        order.eventTime,
        order.eventType,
        order.decoration,
        order.extra,
        order.packageName,
        order.location,
        order.peopleCount,
        order.note,
        order.nameToPrint,
        order.moneyNote,
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return (
        matchesEventType &&
        (!normalizedSearch || searchableText.includes(normalizedSearch))
      );
    });
  }, [data, searchText, selectedEventType]);

  const resetModal = () => {
    setOpen(false);
    setEditingOrder(null);
    form.resetFields();
  };

  const openCreateModal = () => {
    setEditingOrder(null);
    form.resetFields();
    form.setFieldsValue({
      orderDate: dayjs(),
      studioBooked: false,
      prePay: 0,
    });
    setOpen(true);
  };

  const openEditModal = (order: PartyOrder) => {
    setEditingOrder(order);
    form.setFieldsValue({
      ...order,
      orderDate: parseStoredDate(order.orderDate),
      eventDate: parseStoredDate(order.eventDate),
      eventTime: parseStoredTime(order.eventTime),
      prePay: order.prePay || 0,
      studioBooked: Boolean(order.studioBooked),
    });
    setOpen(true);
  };

  const normalizePayload = (values: PartyOrderFormValues) => ({
    ...values,
    orderDate: values.orderDate
      ? dayjs(values.orderDate).format("YYYY.MM.DD")
      : "",
    eventDate: values.eventDate
      ? dayjs(values.eventDate).format("YYYY.MM.DD")
      : "",
    eventTime: values.eventTime ? dayjs(values.eventTime).format("HH:mm") : "",
    prePay: Number(values.prePay || 0),
    studioBooked: Boolean(values.studioBooked),
  });

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      const payload = normalizePayload(values);

      setSubmitting(true);

      if (editingOrder) {
        await api.put(`/party-order/update/${editingOrder._id}`, payload);
        notification.success({
          message: "Амжилттай",
          description: "Event захиалга шинэчлэгдлээ",
        });
      } else {
        await api.post("/party-order/create", payload);
        notification.success({
          message: "Амжилттай",
          description: "Event захиалга бүртгэгдлээ",
        });
      }

      resetModal();
      fetchOrders();
    } catch (error: any) {
      if (error?.errorFields) return;

      notification.error({
        message: "Алдаа",
        description:
          error.response?.data?.message || "Захиалга хадгалахад алдаа гарлаа",
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (id: string) => {
    try {
      setSubmitting(true);
      await api.delete(`/party-order/delete/${id}`);
      notification.success({ message: "Устгагдлаа" });
      fetchOrders();
    } catch (error: any) {
      notification.error({
        message: "Устгахад алдаа гарлаа",
        description: error.response?.data?.message,
      });
    } finally {
      setSubmitting(false);
    }
  };

  const columns: ColumnsType<PartyOrder> = [
    {
      title: "Үйлдэл",
      width: 96,
      fixed: "left",
      render: (_, record) => (
        <Space size={4}>
          <Tooltip title="Засах">
            <Button
              type="text"
              size="small"
              icon={<EditOutlined />}
              onClick={() => openEditModal(record)}
            />
          </Tooltip>
          <Popconfirm
            title="Event захиалга устгах уу?"
            okText="Устгах"
            cancelText="Болих"
            okButtonProps={{ danger: true }}
            onConfirm={() => handleDelete(record._id)}
          >
            <Tooltip title="Устгах">
              <Button
                danger
                type="text"
                size="small"
                icon={<DeleteOutlined />}
              />
            </Tooltip>
          </Popconfirm>
        </Space>
      ),
    },
    {
      title: "Болох өдөр",
      dataIndex: "eventDate",
      width: 120,
      sorter: (a, b) =>
        `${a.eventDate} ${a.eventTime}`.localeCompare(
          `${b.eventDate} ${b.eventTime}`
        ),
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{value}</Typography.Text>
          <Typography.Text type="secondary">{record.eventTime}</Typography.Text>
        </Space>
      ),
    },
    {
      title: "Захиалагч",
      dataIndex: "customerName",
      width: 180,
      ellipsis: true,
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text strong>{value}</Typography.Text>
          {record.peopleCount && (
            <Typography.Text type="secondary">
              {record.peopleCount}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: "Төрөл",
      dataIndex: "eventType",
      width: 130,
      render: (value, record) => (
        <Space direction="vertical" size={4}>
          {value && <Tag color="green">{value}</Tag>}
          {record.childGender && <Tag>{record.childGender}</Tag>}
        </Space>
      ),
    },
    {
      title: "Багц / Байршил",
      dataIndex: "packageName",
      width: 180,
      render: (value, record) => (
        <Space direction="vertical" size={0}>
          <Typography.Text>{value || "-"}</Typography.Text>
          <Typography.Text type="secondary">
            {record.location || "-"}
          </Typography.Text>
        </Space>
      ),
    },
    {
      title: "Чимэглэл",
      dataIndex: "decoration",
      width: 170,
      ellipsis: true,
      render: (value, record) => value || record.extra || "-",
    },
    {
      title: "Студи",
      dataIndex: "studioBooked",
      width: 130,
      render: (value, record) => (
        <Space direction="vertical" size={2}>
          <Tag color={value ? "green" : "default"}>
            {value ? "Захиалсан" : "Үгүй"}
          </Tag>
          {record.studioDateTime && (
            <Typography.Text type="secondary">
              {record.studioDateTime}
            </Typography.Text>
          )}
        </Space>
      ),
    },
    {
      title: "Урьдчилгаа",
      dataIndex: "prePay",
      width: 120,
      align: "right",
      render: toCurrency,
    },
    {
      title: "Нэр бичих",
      dataIndex: "nameToPrint",
      width: 150,
      ellipsis: true,
      render: (value) => value || "-",
    },
    {
      title: "Тэмдэглэл",
      dataIndex: "note",
      width: 220,
      ellipsis: true,
      render: (value, record) => value || record.moneyNote || "-",
    },
    {
      title: "Захиалсан",
      dataIndex: "orderDate",
      width: 120,
      render: (value) => value || "-",
    },
  ];

  return (
    <div className="party-page">
      <div className="party-page-header">
        <div>
          <Typography.Title level={3} className="party-page-title">
            Event захиалга
          </Typography.Title>
          <Typography.Text className="party-page-subtitle">
            Төрсөн өдөр, сэвлэг, event захиалгын бүртгэл
          </Typography.Text>
        </div>

        <Space wrap>
          <Button icon={<ReloadOutlined />} onClick={fetchOrders}>
            Шинэчлэх
          </Button>
          <Button type="primary" icon={<PlusOutlined />} onClick={openCreateModal}>
            Захиалга нэмэх
          </Button>
        </Space>
      </div>

      <div className="party-filter-panel">
        <Row gutter={[12, 8]} align="middle">
          <Col xs={24} md={10}>
            <Input.Search
              allowClear
              placeholder="Нэр, өдөр, багц, тэмдэглэлээр хайх"
              onChange={(event) => setSearchText(event.target.value)}
            />
          </Col>
          <Col xs={24} md={6}>
            <Select
              allowClear
              showSearch
              optionFilterProp="label"
              placeholder="Төрөл сонгох"
              options={eventTypeOptions}
              value={selectedEventType}
              onChange={(value) => setSelectedEventType(value || null)}
              style={{ width: "100%" }}
            />
          </Col>
          <Col xs={24} md={8}>
            <div className="party-summary">
              <span>Нийт: {data.length}</span>
              <span>Харагдаж буй: {filteredData.length}</span>
            </div>
          </Col>
        </Row>
      </div>

      <div className="party-table-panel">
        <Table
          rowKey="_id"
          loading={loading}
          dataSource={filteredData}
          columns={columns}
          scroll={{ x: 1500 }}
          pagination={{
            pageSize: 50,
            showSizeChanger: true,
            pageSizeOptions: ["20", "50", "100"],
            position: ["bottomCenter"],
            showTotal: (total, range) => `${range[0]}-${range[1]} / ${total}`,
          }}
        />
      </div>

      <Modal
        title={editingOrder ? "Event захиалга засах" : "Event захиалга бүртгэх"}
        open={open}
        onCancel={resetModal}
        onOk={handleSubmit}
        okText={editingOrder ? "Хадгалах" : "Бүртгэх"}
        cancelText="Болих"
        confirmLoading={submitting}
        width={960}
        destroyOnClose
      >
        <Form form={form} layout="vertical" className="party-form">
          <div className="party-form-section">
            <Typography.Title level={5}>Үндсэн мэдээлэл</Typography.Title>
            <Row gutter={[12, 8]}>
              <Col xs={24} md={8}>
                <Form.Item label="Захиалга өгсөн огноо" name="orderDate">
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Болох өдөр"
                  name="eventDate"
                  rules={[{ required: true, message: "Болох өдөр сонгоно уу" }]}
                >
                  <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Цаг"
                  name="eventTime"
                  rules={[{ required: true, message: "Цаг сонгоно уу" }]}
                >
                  <TimePicker
                    format="HH:mm"
                    minuteStep={5}
                    style={{ width: "100%" }}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item
                  label="Нэр / Хаяг"
                  name="customerName"
                  rules={[{ required: true, message: "Нэр эсвэл хаяг оруулна уу" }]}
                >
                  <Input placeholder="Захиалагчийн нэр, хаяг" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Хэдэн хүнтэй" name="peopleCount">
                  <Input placeholder="20 том хүн, 10 хүүхэд" />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="party-form-section">
            <Typography.Title level={5}>Event мэдээлэл</Typography.Title>
            <Row gutter={[12, 8]}>
              <Col xs={24} md={8}>
                <Form.Item label="Төрөл" name="eventType">
                  <Select
                    allowClear
                    showSearch
                    optionFilterProp="label"
                    options={eventTypeOptions}
                  />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Хүү / Охин" name="childGender">
                  <Select allowClear options={genderOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Чимэглэл" name="decoration">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Сонгосон багц" name="packageName">
                  <Select allowClear options={packageOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Хаана" name="location">
                  <Select allowClear options={locationOptions} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Нэмэлт" name="extra">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="party-form-section">
            <Typography.Title level={5}>Төлбөр ба студи</Typography.Title>
            <Row gutter={[12, 8]}>
              <Col xs={24} md={8}>
                <Form.Item label="Урьдчилгаа" name="prePay">
                  <InputNumber min={0} style={{ width: "100%" }} />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item
                  label="Студи цаг захиалсан эсэх"
                  name="studioBooked"
                  valuePropName="checked"
                >
                  <Switch checkedChildren="Тийм" unCheckedChildren="Үгүй" />
                </Form.Item>
              </Col>
              <Col xs={24} md={8}>
                <Form.Item label="Авхуулах өдөр цаг" name="studioDateTime">
                  <Input placeholder="Жишээ: 2026.05.10 14:00" />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Нэр бичлүүлэх" name="nameToPrint">
                  <Input />
                </Form.Item>
              </Col>
              <Col xs={24} md={12}>
                <Form.Item label="Мөнгө / тэмдэглэл" name="moneyNote">
                  <Input />
                </Form.Item>
              </Col>
            </Row>
          </div>

          <div className="party-form-section">
            <Form.Item label="Тайлбар" name="note">
              <Input.TextArea rows={3} placeholder="Нэмэлт тэмдэглэл" />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </div>
  );
};

export default PartyOrderPage;
